import React, { useState, useEffect } from "react";
import { ResourceLine, resourceLines } from "app/utils/request/scheduling/facilityResources";
import { DateTime } from "luxon";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { schedulingFetchPatch } from "app/utils/request/scheduling/fetch";
import InfoModal from "app/routes/Calendar/InfoModal";
import { resetBookSession } from "app/actions/scheduling/bookSession";

import PanelWrapper from "app/components/PanelWrapper";
import PublicWrapper from "app/components/PublicWrapper";
import { RootState } from "typedefs";
import { BookSessionState } from "app/reducers/scheduling/bookSession";
import { Spinner } from "app/components/Loading";

type BookingData = {
  startHour: number;
  timeZone: string;
};

type CoachInfo = {
  name: string;
  profilePicture?: string;
};


export default function PaymentConfirmation() {
  const { search } = useLocation();
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [errors, setErrors] = useState([]);

  const searchParams = new URLSearchParams(search);
  const timeSlotReservationId = searchParams.get("time_slot_reservation_id");
  const isMobile = ["true"].includes(
    (searchParams.get("mobile") || "").toLowerCase()
  );

  const bookSession = useSelector(
    (state: RootState): BookSessionState => state.bookSession
  );

  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [bookedResourceLines, setBookedResourceLines] = useState<ResourceLine[]>([]);
  const [coachInfo, setCoachInfo] = useState<CoachInfo | null>(null);

  // Extract coach info from bookSession (before it's reset)
  const extractCoachInfo = (): CoachInfo | null => {
    const coach = bookSession?.coach;
    if (!coach) {
      return null;
    }

    let name = coach.fullName;
    if (!name && (coach.firstName || coach.lastName)) {
      name = [coach.firstName, coach.lastName].filter(Boolean).join(' ');
    }

    if (!name || coach.id === 'all-coaches') {
      return null;
    }

    return {
      name,
      profilePicture: coach.profilePicture,
    };
  };

  useEffect(() => {
    createSession();
  }, []);

  const bookingSessionSuccess = () => {
    history.push("/sessions");
    dispatch(resetBookSession());
  };

  const createSession = () => {
    schedulingFetchPatch(`/time_slot_reservations/${timeSlotReservationId}/finalize`)
      .then((resp) => {
        const startISO = resp?.start;
        const tzId = resp.timeZone;
        const startMillis = startISO
          ? DateTime.fromISO(startISO).toMillis()
          : Number(bookSession?.time?.startHour);

        const tz = tzId || bookSession?.coach?.timeZone || undefined;

        setBookingData({ startHour: startMillis, timeZone: tz });
        setBookedResourceLines(resourceLines(resp));

        // Capture coach info before resetting state
        setCoachInfo(extractCoachInfo());

        if (isMobile) {
          const qs = new URLSearchParams();
          qs.set("start", String(startMillis));
          const offset = DateTime.fromMillis(startMillis).setZone(tz).toFormat("ZZ");
          qs.set("offset", offset);
          history.replace(`/purchase/session/success?${qs.toString()}`);
          return;
        }
        setShowPopup(true);
        setLoading(false);
        setErrors(null);
        dispatch(resetBookSession());
      })
      .catch((errs) => {
        setLoading(false);
        setErrors(errs.errors);
      });
  };

  return (
    <PublicWrapper>
      <PanelWrapper>
        <div className="text-center mt-10 flex flex-col items-center gap-4">
          <div>{t("Processing your payment, please wait...")}</div>
          <br/>
          <Spinner />
        </div>

        { (!loading && !errors) && (
          <InfoModal
            isOpen={showPopup}
            title={t("Session Booked")}
            status="success"
            onDismiss={bookingSessionSuccess}
          >
            <div className="flex flex-col items-center gap-3">
              <div>{t("You have successfully booked a new session")}</div>
              {coachInfo && (
                <div className="flex items-center gap-2">
                  {coachInfo.profilePicture && (
                    <img
                      src={coachInfo.profilePicture}
                      alt={coachInfo.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  )}
                  <div className="font-semibold">{coachInfo.name}</div>
                </div>
              )}
              <div>
                <b>
                  {DateTime.fromMillis(bookingData.startHour)
                    .setZone(bookingData.timeZone)
                    .toLocaleString(DateTime.DATE_HUGE)}
                </b>{" "}
                {t("at")}
                <b>
                  {" "}
                  {DateTime.fromMillis(bookingData.startHour)
                    .setZone(bookingData.timeZone)
                    .toLocaleString({
                      ...DateTime.TIME_WITH_SHORT_OFFSET,
                      second: undefined,
                    })}
                </b>
              </div>
              {bookedResourceLines.map((line) => (
                <div data-testid="booked-resource-row" key={line.section}>
                  {line.section ? <>{line.section}: </> : null}
                  <b>{line.titles}</b>
                </div>
              ))}
            </div>
          </InfoModal>
        )}



        { (!loading && errors) && (
          <InfoModal
            isOpen={errors?.length > 0}
            title={errors[0]?.details[0]?.message}
            status="error"
            onDismiss={() => history.push("/sessions/new")}
          >
            <div className="flex flex-col items-center">
              <div>{errors[0]?.details[0]?.detail}</div>
            </div>
          </InfoModal>
        )}
      </PanelWrapper>
    </PublicWrapper>
  );
}
